import React from 'react'
import { supabase } from '@/lib/supabase'
import { applyOptimisticReactionToggle } from '@/shared/utils/reactions'
import {
  fetchAnalysisCardReactions,
  toggleAnalysisCardReaction,
} from '../services/analysisCommunityService'
import type { CommunityReaction } from '@/modules/comunidade/services/communityService'

type UseAnalysisCardReactionsResult = {
  loading: boolean
  reactions: CommunityReaction[]
  toggleEmoji: (emoji: string) => Promise<void>
  reload: () => Promise<void>
}

export function useAnalysisCardReactions(
  analysisId: number | null,
  active: boolean,
): UseAnalysisCardReactionsResult {
  const [loading, setLoading] = React.useState(false)
  const [reactions, setReactions] = React.useState<CommunityReaction[]>([])

  const load = React.useCallback(async () => {
    if (!analysisId || !active) {
      setReactions([])
      return
    }

    setLoading(true)
    try {
      const data = await fetchAnalysisCardReactions(analysisId)
      setReactions(data)
    } catch {
      setReactions([])
    } finally {
      setLoading(false)
    }
  }, [active, analysisId])

  React.useEffect(() => {
    if (!analysisId || !active) {
      setReactions([])
      return
    }

    let alive = true
    let interval: any = null

    ;(async () => {
      await load()
      if (!alive) return
    })()

    const channel = supabase
      .channel(`analysis_card:${analysisId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'analysis_community_reactions',
          filter: `analysis_id=eq.${analysisId}`,
        } as any,
        () => {
          load().catch(() => {
            // erros de reload sÇœo silenciosamente ignorados; o prÇüximo evento atualiza o estado.
          })
        },
      )
      .subscribe()

    // Fallback: pequeno polling para garantir atualizaÇõÇæes mesmo se o canal realtime tiver algum problema.
    interval = setInterval(() => {
      load().catch(() => {
        // silencioso; prÇüximo ciclo corrige
      })
    }, 3500)

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // noop
      }
      try {
        if (interval) clearInterval(interval)
      } catch {
        // noop
      }
      alive = false
    }
  }, [active, load, analysisId])

  const toggleEmoji = React.useCallback(
    async (emoji: string) => {
      if (!analysisId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      setReactions((prev) => applyOptimisticReactionToggle(prev, trimmed))

      void toggleAnalysisCardReaction(analysisId, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[Analysis] toggleAnalysisCardReaction error', error)
        load().catch(() => {
          // silencioso; próximo ciclo/realtime corrige
        })
      })
    },
    [active, analysisId, load],
  )

  return {
    loading,
    reactions,
    toggleEmoji,
    reload: load,
  }
}
